import { theme } from '@prophecy/ui-v3/index';
import { useState, useEffect } from 'react';
import styled from 'styled-components';

const TYPING_REFRESH_RATE_MS = 50;
/**
 * TypingText is a component that displays a text with a typing effect.
 * @param text - The text to display.
 * @returns A component that displays a text with a typing effect.
 */

export const TypingText = ({ text }: { text: string | null }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const isTyping = Boolean(text && currentIndex < text.length);
  const isTypingComplete = Boolean(text && currentIndex > text.length);

  useEffect(() => {
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (isTypingComplete) {
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentIndex((currentIndex) => currentIndex + 1);
    }, TYPING_REFRESH_RATE_MS);
    return () => clearInterval(intervalId);
  }, [text, isTypingComplete]);

  if (!text) {
    // EmptyText is a span with a non-breaking space to prevent layout shift
    return <EmptyText>&nbsp;</EmptyText>;
  }
  return (
    <AnimatedTypingText>
      {text.slice(0, currentIndex)}
      {isTyping && <Cursor />}
    </AnimatedTypingText>
  );
};

const EmptyText = styled.span`
  display: inline-block;
  min-width: 150px;
  color: ${theme.colors.brand700};
  font-weight: ${theme.fontWeight.medium};
  border-bottom: 2px solid ${theme.colors.brand500};
  margin: 0 ${theme.spaces.x4};
  position: relative;
  text-align: center;
`;

const AnimatedTypingText = styled(EmptyText)`
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: ${theme.lineHeight.x16};
  background-color: ${theme.colors.brand500};
  margin-left: ${theme.spaces.x2};
  animation: blink 1s step-end infinite;
  vertical-align: middle;

  @keyframes blink {
    from,
    to {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;
